import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatMessage } from "shared";
import { cn } from "@/lib/utils";

interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Individual message bubble with markdown rendering
 * Properly handles long content with good formatting
 */
export function MessageBubble({
  message,
  isStreaming = false,
  className,
  style,
}: MessageBubbleProps) {
  const isUser = message.type === "USER_MESSAGE";
  const isAdmin = message.type === "ADMIN_MESSAGE";

  // Format timestamp
  const timestamp = new Date(message.created_at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Check if this message is currently being streamed
  const isMessageStreaming =
    isStreaming || (message.metadata as { isStreaming?: boolean })?.isStreaming;

  return (
    <div
      className={cn(
        "flex flex-col gap-1 w-full",
        isUser ? "items-end" : "items-start",
        // Animation
        "animate-in fade-in slide-in-from-bottom-2 duration-200",
        className,
      )}
      style={style}
    >
      {/* Admin name badge */}
      {isAdmin && message.sender_name && (
        <span className="text-xs text-muted-foreground font-medium px-1">
          {message.sender_name}
        </span>
      )}

      <div
        className={cn(
          "rounded-2xl px-4 py-3",
          // Max width for bubbles - wider for AI to show content better
          isUser ? "max-w-[80%]" : "max-w-[90%]",
          // User messages: right aligned
          isUser && "rounded-br-md",
          // Assistant/Admin messages: left aligned
          !isUser && "rounded-bl-md",
        )}
        style={{
          backgroundColor: isUser
            ? "var(--widget-user-bubble)"
            : isAdmin
              ? "var(--widget-admin-bubble)"
              : "var(--widget-ai-bubble)",
          color: isUser
            ? "var(--widget-user-bubble-text)"
            : isAdmin
              ? "white" // Admin bubble text is always white
              : "var(--widget-ai-bubble-text)",
        }}
      >
        {/* User messages: plain text */}
        {isUser ? (
          <p className="text-[15px] whitespace-pre-wrap break-words leading-relaxed">
            {message.content}
          </p>
        ) : (
          /* Assistant/Admin messages: markdown with proper formatting */
          <div className="ai-message-content text-[15px] leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Paragraphs
                p: ({ children }) => (
                  <p className="mb-3 last:mb-0 leading-relaxed whitespace-pre-wrap">
                    {children}
                  </p>
                ),
                // Unordered lists
                ul: ({ children }) => (
                  <ul className="list-disc pl-5 mb-3 space-y-1.5">
                    {children}
                  </ul>
                ),
                // Ordered lists
                ol: ({ children }) => (
                  <ol className="list-decimal pl-5 mb-3 space-y-1.5">
                    {children}
                  </ol>
                ),
                // List items
                li: ({ children }) => (
                  <li className="leading-relaxed pl-1">{children}</li>
                ),
                // Code blocks and inline code
                code: ({ className, children, ...props }) => {
                  const isInline = !className;
                  return isInline ? (
                    <code
                      className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded text-[13px] font-mono break-all"
                      {...props}
                    >
                      {children}
                    </code>
                  ) : (
                    <code
                      className="block bg-black/10 dark:bg-white/10 p-3 rounded-lg text-[13px] overflow-x-auto font-mono whitespace-pre"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                // Pre blocks (code block wrapper)
                pre: ({ children }) => (
                  <pre className="mb-3 last:mb-0 overflow-x-auto">
                    {children}
                  </pre>
                ),
                // Links
                a: ({ children, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:opacity-80 font-medium break-all"
                  >
                    {children}
                  </a>
                ),
                // Bold
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
                // Headings
                h1: ({ children }) => (
                  <h1 className="text-lg font-bold mb-2 mt-4 first:mt-0">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-base font-bold mb-2 mt-3 first:mt-0">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-sm font-bold mb-2 mt-3 first:mt-0">
                    {children}
                  </h3>
                ),
                // Blockquotes
                blockquote: ({ children }) => (
                  <blockquote className="border-l-3 border-current/30 pl-3 my-3 opacity-90 italic">
                    {children}
                  </blockquote>
                ),
                // Horizontal rule
                hr: () => <hr className="my-4 border-current/20" />,
                // Tables
                table: ({ children }) => (
                  <div className="overflow-x-auto my-3">
                    <table className="min-w-full text-sm border-collapse">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-black/5 dark:bg-white/5">
                    {children}
                  </thead>
                ),
                th: ({ children }) => (
                  <th className="px-3 py-2 text-left font-semibold border-b border-current/20">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="px-3 py-2 border-b border-current/10">
                    {children}
                  </td>
                ),
              }}
            >
              {message.content || ""}
            </ReactMarkdown>

            {/* Streaming cursor indicator */}
            {isMessageStreaming && (
              <span className="streaming-cursor" aria-hidden="true" />
            )}
          </div>
        )}
      </div>

      {/* Timestamp - only show when not streaming */}
      {!isMessageStreaming && (
        <span className="text-[10px] text-muted-foreground/70 px-1">
          {timestamp}
        </span>
      )}
    </div>
  );
}
